import { Plus, Settings, Ban, ArrowUp, ArrowDown, ChevronsUp } from 'lucide-react';
import type { ModelConfigData } from './types';

export type ModelMoveDirection = 'up' | 'down' | 'top';

interface ModelsTabProps {
  models: ModelConfigData[];
  movingId: string | null;
  onMove: (id: string, direction: ModelMoveDirection) => void;
  onAdd: () => void;
  onEdit: (model: ModelConfigData) => void;
  onConfigureRouting: (model: ModelConfigData) => void;
  onDelete: (id: string) => void;
}

interface SortCellProps {
  index: number;
  total: number;
  disabled: boolean;
  onMove: (id: string, direction: ModelMoveDirection) => void;
  modelId: string;
}

const SortControls = ({ index, total, disabled, onMove, modelId }: SortCellProps) => {
  const canMoveUp = index > 0;
  const canMoveDown = index < total - 1;
  const buttonClass = (canClick: boolean) =>
    `p-1.5 rounded-lg transition-colors ${
      canClick && !disabled
        ? 'text-light-muted hover:bg-light-hover hover:text-light-text'
        : 'text-gray-300 cursor-not-allowed'
    }`;

  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-flex min-w-[24px] items-center justify-center rounded-md bg-light-hover px-1.5 py-0.5 text-xs font-medium text-light-text">
        {index + 1}
      </span>
      <div className="flex items-center gap-0.5">
        <button
          onClick={() => onMove(modelId, 'top')}
          disabled={!canMoveUp || disabled}
          className={buttonClass(canMoveUp)}
          title="置顶"
        >
          <ChevronsUp className="h-4 w-4" />
        </button>
        <button
          onClick={() => onMove(modelId, 'up')}
          disabled={!canMoveUp || disabled}
          className={buttonClass(canMoveUp)}
          title="上移"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
        <button
          onClick={() => onMove(modelId, 'down')}
          disabled={!canMoveDown || disabled}
          className={buttonClass(canMoveDown)}
          title="下移"
        >
          <ArrowDown className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const getChannelDisplay = (model: ModelConfigData) => {
  const fallbackChannelIds = Array.isArray(model.fallbackChannelIds) ? model.fallbackChannelIds : [];
  const fallbackCount = fallbackChannelIds.length;
  const rows = [
    { key: 'global', label: '兜底', enabled: true, channel: fallbackCount > 0 ? { name: `${fallbackCount} 级`, apiModelId: null } : null },
    { key: 'text2img', label: '文生图', enabled: model.enableText2img, channel: model.text2imgChannel },
    { key: 'img2img', label: '图生图', enabled: model.enableImg2img, channel: model.img2imgChannel },
    { key: 'inpaint', label: '局部重绘', enabled: model.enableInpaint, channel: model.inpaintChannel },
  ] as const;

  return (
    <div className="flex min-w-[240px] flex-col gap-1.5 text-left">
      {rows.map((row) => (
        <div key={row.key} className="flex flex-wrap items-center gap-1.5 text-xs leading-5">
          <span
            className={`inline-flex max-w-full items-center gap-1 rounded border px-2 py-0.5 text-xs whitespace-nowrap ${
              row.enabled
                ? row.channel
                  ? 'border-blue-300 bg-transparent text-blue-600'
                  : 'border-red-300 bg-transparent text-red-500'
                : 'border-gray-300 bg-transparent text-gray-400'
            }`}
            title={
              row.enabled
                ? row.channel
                  ? `${row.label} ${row.channel.name}${row.channel.apiModelId ? ` ${row.channel.apiModelId}` : ''}`
                  : `${row.label} 未配置`
                : `${row.label} 未启用`
            }
          >
            <span>{row.label}</span>
            {row.enabled ? (
              row.channel ? (
                <span className="truncate">
                  {row.channel.name}
                  {row.channel.apiModelId ? ` ${row.channel.apiModelId}` : ''}
                </span>
              ) : (
                <span>未配置</span>
              )
            ) : (
              <span>未启用</span>
            )}
          </span>
          {row.key === 'global' ? (
            <span
              className={`inline-flex items-center rounded border px-2 py-0.5 text-[11px] whitespace-nowrap ${
                model.enableFallbackChain
                  ? 'border-emerald-300 text-emerald-600'
                  : 'border-gray-300 text-gray-400'
              }`}
              title={model.enableFallbackChain ? '路由降级已开启，失败时会继续尝试后续渠道' : '路由降级已关闭，只使用首选渠道'}
            >
              {model.enableFallbackChain ? '降级开启' : '仅首选'}
            </span>
          ) : null}
        </div>
      ))}
      <div className="pt-0.5">
        <span className="text-xs text-light-muted">
          覆盖规则 {model.routingSummary?.overrideCount ?? 0} 条
        </span>
      </div>
    </div>
  );
};

// 获取生成模式显示
const getGenerationModesDisplay = (model: ModelConfigData) => {
  const modes: { key: string; label: string; credits: number }[] = [];

  if (model.enableText2img) {
    modes.push({ key: 'text2img', label: '文生图', credits: model.text2imgCredits });
  }
  if (model.enableImg2img) {
    modes.push({ key: 'img2img', label: '图生图', credits: model.img2imgCredits });
  }
  if (model.enableInpaint) {
    modes.push({ key: 'inpaint', label: '局部重绘', credits: model.inpaintCredits });
  }

  if (modes.length === 0) {
    return <span className="text-xs text-light-muted">未启用</span>;
  }

  return (
    <div className="flex flex-wrap gap-1 justify-center">
      {modes.map((mode) => (
        <span
          key={mode.key}
          className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-600 whitespace-nowrap"
        >
          {mode.label} +{mode.credits}
        </span>
      ))}
    </div>
  );
};

export default function ModelsTab({ models, movingId, onMove, onAdd, onEdit, onConfigureRouting, onDelete }: ModelsTabProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-light-text">模型配置</h2>
          <p className="text-sm text-light-muted">管理图片生成模型及其积分价格</p>
        </div>
        <button
          onClick={onAdd}
          className="bg-accent-blue text-white font-medium px-4 py-2 rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          添加模型
        </button>
      </div>

      {/* Model List */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden xl:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-light-hover">
              <tr>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">排序</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">模型</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">模型ID</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">标准(1K)</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">高清(2K)</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">超清(4K)</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">生成模式</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">模型增强</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">API渠道配置</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">状态</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">操作</th>
              </tr>
            </thead>
            <tbody>
              {models.map((model, index) => (
                <tr key={model.id} className="border-t border-light-border">
                  <td className="px-4 py-3">
                    <div className="flex justify-center">
                      <SortControls
                        index={index}
                        total={models.length}
                        disabled={movingId !== null}
                        onMove={onMove}
                        modelId={model.id}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="max-w-[180px]">
                      <p className="text-sm font-medium text-light-text truncate" title={model.name}>{model.name}</p>
                      {model.description && (
                        <p className="text-xs text-light-muted truncate" title={model.description}>{model.description}</p>
                      )}
                      {model.appendPrompt && (
                        <p className="mt-1 text-xs text-amber-600 truncate" title={model.appendPrompt}>附加提示词已配置</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <code className="text-xs bg-light-hover px-2 py-1 rounded">{model.modelId}</code>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm text-light-text">{model.standardCredits} 积分</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${model.enableStandardSize !== false ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                        {model.enableStandardSize !== false ? '已开启' : '已关闭'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm text-light-text">{model.hdCredits} 积分</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${model.enableHdSize !== false ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                        {model.enableHdSize !== false ? '已开启' : '已关闭'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm text-light-text">{model.uhdCredits} 积分</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${model.enableUhdSize !== false ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                        {model.enableUhdSize !== false ? '已开启' : '已关闭'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getGenerationModesDisplay(model)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getChannelDisplay(model)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-lg text-xs ${
                      model.isActive
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {model.isActive ? '启用' : '禁用'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => onEdit(model)}
                        className="p-2 text-accent-blue hover:bg-blue-50 rounded-lg transition-colors"
                        title="编辑"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onConfigureRouting(model)}
                        className="rounded-lg border border-light-border px-2.5 py-1.5 text-xs text-light-text transition-colors hover:bg-light-hover"
                        title="路由"
                      >
                        路由
                      </button>
                      <button
                        onClick={() => onDelete(model.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="删除"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="xl:hidden divide-y divide-light-border">
          {models.map((model, index) => (
            <div key={model.id} className="p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-light-text">{model.name}</p>
                  <div className="flex flex-col gap-0.5">
                    <code className="text-xs bg-light-hover px-2 py-0.5 rounded text-light-muted">{model.modelId}</code>
                  </div>
                  {model.description && (
                    <p className="text-xs text-light-muted mt-1">{model.description}</p>
                  )}
                  {model.appendPrompt && (
                    <p className="mt-1 text-xs text-amber-600">附加提示词已配置</p>
                  )}
                </div>
                <span className={`px-2 py-1 rounded-lg text-xs shrink-0 ml-2 ${
                  model.isActive
                    ? 'bg-green-100 text-green-600'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {model.isActive ? '启用' : '禁用'}
                </span>
              </div>

              {/* Credits Grid */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className={`rounded-lg p-2 text-center ${model.enableStandardSize !== false ? 'bg-light-hover' : 'bg-gray-100'}`}>
                  <p className="text-xs text-light-muted">标准(1K)</p>
                  <p className="text-sm font-medium text-light-text">{model.standardCredits} 积分</p>
                  <span className={`text-xs ${model.enableStandardSize !== false ? 'text-green-600' : 'text-gray-500'}`}>
                    {model.enableStandardSize !== false ? '已开启' : '已关闭'}
                  </span>
                </div>
                <div className={`rounded-lg p-2 text-center ${model.enableHdSize !== false ? 'bg-light-hover' : 'bg-gray-100'}`}>
                  <p className="text-xs text-light-muted">高清(2K)</p>
                  <p className="text-sm font-medium text-light-text">{model.hdCredits} 积分</p>
                  <span className={`text-xs ${model.enableHdSize !== false ? 'text-green-600' : 'text-gray-500'}`}>
                    {model.enableHdSize !== false ? '已开启' : '已关闭'}
                  </span>
                </div>
                <div className={`rounded-lg p-2 text-center ${model.enableUhdSize !== false ? 'bg-light-hover' : 'bg-gray-100'}`}>
                  <p className="text-xs text-light-muted">超清(4K)</p>
                  <p className="text-sm font-medium text-light-text">{model.uhdCredits} 积分</p>
                  <span className={`text-xs ${model.enableUhdSize !== false ? 'text-green-600' : 'text-gray-500'}`}>
                    {model.enableUhdSize !== false ? '已开启' : '已关闭'}
                  </span>
                </div>
              </div>

              {/* Modes */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {getGenerationModesDisplay(model)}
              </div>

              {/* Channels */}
              <div className="mb-3">
                <p className="text-xs text-light-muted mb-1">API渠道配置</p>
                {getChannelDisplay(model)}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2">
                <SortControls
                  index={index}
                  total={models.length}
                  disabled={movingId !== null}
                  onMove={onMove}
                  modelId={model.id}
                />
                <div className="flex gap-2 ml-auto">
                  <button
                    onClick={() => onEdit(model)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-accent-blue bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    编辑
                  </button>
                  <button
                    onClick={() => onConfigureRouting(model)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-light-text bg-light-hover hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    路由
                  </button>
                  <button
                    onClick={() => onDelete(model.id)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <Ban className="w-4 h-4" />
                    删除
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
